import React, { useState, useEffect } from "react";
import { db } from "../firebase/config";
import { updateDoc, doc, collection, query, where, getDocs, addDoc, arrayUnion } from "firebase/firestore";
import { Plus, Search, Truck, Loader2, ChevronDown, ChevronUp, Edit2, FileText, Sliders, CheckSquare, DollarSign } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useLogistics } from "../hooks/useLogistics";
import { useZones } from "../hooks/useZones";
import { usePOs } from "../hooks/usePOs";
import { useCompanyLogos } from "../hooks/useCompanyLogos";
import ChangeRequestModal from "../components/modals/ChangeRequestModal";
import DispatchShipmentModal from "../components/modals/DispatchShipmentModal";
import ReceiveItemModal from "../components/modals/ReceiveItemModal";
import ReceiveBatchModal from "../components/modals/ReceiveBatchModal";
import DeliveryDateModal from "../components/modals/DeliveryDateModal";
import POTasksModal from "../components/modals/POTasksModal";
import EditShippingCostModal from "../components/modals/EditShippingCostModal";
import { printPackingSlip, getAllPackingSlipsForPO } from "../utils/packingSlipExport";
import { isPoDelivered, isShipmentDelivered, getPoShippingRows } from "../utils/deliveryStatus";
import { getPoShippingCostOut } from "../utils/pricingUtils";
import { logSecurityEvent } from "../services/AuditLogger";

export default function Logistics() {
  const { currentUser } = useAuth();
  const { shipments, carriers: carrierObjects, loading: logLoading } = useLogistics();
  const { zones } = useZones();
  const { pos, updatePO } = usePOs();
  const { companyLogos } = useCompanyLogos();
  const carriers = carrierObjects.map(c => c.name || c);

  const loading = logLoading;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [companyFilter, setCompanyFilter] = useState("All");
  const [selectedChangeRequestPo, setSelectedChangeRequestPo] = useState(null);
  const [selectedTasksPo, setSelectedTasksPo] = useState(null);

  const isPoFullyDelivered = (poToCheck) => {
    if (!poToCheck || !poToCheck.items || poToCheck.items.length === 0) return false;
    const linkedShipment = shipments.find(s => s.poId === poToCheck.id);
    if (!linkedShipment) return false;
    return poToCheck.items.every(item => {
      const itemStatus = linkedShipment.itemStatuses?.[item.id]?.status || linkedShipment.status || "To Order";
      return itemStatus === "Delivered";
    });
  };

  const getPoLogoUrl = (po) => {
    if (po?.logoUrl) return po.logoUrl;
    const activeLogo = companyLogos.find(l => l.isActive);
    return activeLogo?.url || "";
  };

  const getCompanyNameForPo = (po) => {
    if (!po?.logoUrl) {
      const activeLogo = companyLogos.find(l => l.isActive);
      return activeLogo?.name || "No Company";
    }
    const logo = companyLogos.find(l => l.url === po.logoUrl);
    return logo?.name || "Unknown Company";
  };
  const [modalOpen, setModalOpen] = useState(false);
  const [expandedShipments, setExpandedShipments] = useState({});
  const [editingCarrierId, setEditingCarrierId] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    trackingNumber: "", carrier: "DHL Express", origin: "", destination: "", estimatedDelivery: "", status: "To Order"
  });

  // Receive single item modal
  const [receiveModalOpen, setReceiveModalOpen] = useState(false);
  const [receiveContext, setReceiveContext] = useState(null); // { shipment, item }

  // Receive batch modal (Progress Order Status to Warehouse)
  const [receiveBatchModalOpen, setReceiveBatchModalOpen] = useState(false);
  const [receiveBatchContext, setReceiveBatchContext] = useState(null); // { shipment, itemsToReceive, nonWarehouseUpdates }

  // Delivery confirmation modal
  const [deliveryModalOpen, setDeliveryModalOpen] = useState(false);
  const [deliveryContext, setDeliveryContext] = useState(null); // { shipment, item, currentItemStatus }

  // Edit Shipping Cost Modal states
  const [editCostModalOpen, setEditCostModalOpen] = useState(false);
  const [editCostContext, setEditCostContext] = useState(null);

  const statuses = ["To Order", "Purchased", "Transport -> NC", "Warehouse", "Transport -> Client", "Delivered"];

  const toggleAccordion = (id) => setExpandedShipments(prev => ({ ...prev, [id]: !prev[id] }));

  const handleGeneratePackingSlip = (shipment, batch = null, batchIndex = null, totalBatches = 1) => {
    const po = getPo(shipment?.poId);
    const activeLogoUrl = getPoLogoUrl(po);
    printPackingSlip({
      shipment,
      po,
      batch,
      batchIndex,
      totalBatches,
      companyLogoUrl: activeLogoUrl
    });
  };

  const getPo = (poId) => pos.find(p => p.id === poId);
  const getPoItems = (poId) => getPo(poId)?.items || [];

  // Get per-item status: uses itemStatuses map, falls back to shipment-level status (normalizes legacy 'Transport -> HQ' to 'Transport -> NC')
  const getItemStatus = (shipment, itemId) => {
    const raw = shipment.itemStatuses?.[itemId]?.status || shipment.status || "To Order";
    return raw === "Transport -> HQ" ? "Transport -> NC" : raw;
  };

  // Check if all items in a shipment are delivered
  const isAllDelivered = (shipment) => {
    const poItems = getPoItems(shipment.poId);
    if (!poItems.length) return (shipment.status || "To Order") === "Delivered";
    return poItems.every(item => {
      const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [item];
      return itemVendors.every(v => {
        const key = v.id && v.id !== 'default' ? `${item.id}_${v.id}` : item.id;
        return getItemStatus(shipment, key) === "Delivered";
      });
    });
  };

  // Compute header aggregate status from all items
  const getHeaderStatus = (shipment, poItems) => {
    if (!poItems.length) return shipment.status || "To Order";
    const itemStats = [];
    poItems.forEach(item => {
      const itemVendors = item.vendors && item.vendors.length > 0 ? item.vendors : [item];
      itemVendors.forEach(v => {
        const key = v.id && v.id !== 'default' ? `${item.id}_${v.id}` : item.id;
        itemStats.push(getItemStatus(shipment, key));
      });
    });
    const unique = [...new Set(itemStats)];
    if (unique.length === 1) return unique[0];
    const indices = unique.map(s => statuses.indexOf(s)).filter(i => i >= 0);
    const lowest = statuses[Math.min(...indices)];
    return `${lowest} (Mixed)`;
  };

  function handleOpenAddModal() {
    const trackNum = `TRK-${Math.floor(100000 + Math.random() * 900000)}`;
    const savedAddressObj = localStorage.getItem("facility_address");
    let defaultDestination = "";
    if (savedAddressObj) {
      try {
        const addr = JSON.parse(savedAddressObj);
        defaultDestination = [addr.address1, addr.address2, addr.city, addr.state, addr.zip, addr.country].filter(Boolean).join(", ");
      } catch (e) { console.error("Failed to parse facility address:", e); }
    }
    setFormData({
      trackingNumber: trackNum, carrier: carriers[0] || "DHL Express", origin: "", destination: defaultDestination,
      estimatedDelivery: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split("T")[0], status: "To Order"
    });
    setModalOpen(true);
  }

  // Advance a single item's status
  async function handleItemAdvance(shipment, item, currentItemStatus) {
    const normalizedCurrent = currentItemStatus === "Transport -> HQ" ? "Transport -> NC" : currentItemStatus;
    const currentIndex = statuses.indexOf(normalizedCurrent);
    if (currentIndex === -1 || currentIndex === statuses.length - 1) return;
    const nextStatus = statuses[currentIndex + 1];

    // Intercept transition to Warehouse → open receiving modal for this item
    if (nextStatus === "Warehouse") {
      setReceiveContext({ shipment, item });
      setReceiveModalOpen(true);
      return;
    }

    // Intercept transition to Delivered → open delivery date confirmation modal
    if (nextStatus === "Delivered") {
      setDeliveryContext({ shipment, item, currentItemStatus });
      setDeliveryModalOpen(true);
      return;
    }

    try {
      const logRef = doc(db, "logistics", shipment.id);
      const key = item.vendorKey || item.id;
      const updates = { [`itemStatuses.${key}.status`]: nextStatus };
      if (currentItemStatus === "To Order" && nextStatus === "Purchased") {
        const today = new Date().toISOString().split("T")[0];
        updates[`itemStatuses.${key}.orderedDate`] = today;
        const vName = item.vendorName || item.vendor;
        if (vName) {
          try {
            const vendorsQuery = query(collection(db, "vendors"), where("name", "==", vName));
            const vendorsSnapshot = await getDocs(vendorsQuery);
            vendorsSnapshot.forEach(async (vendorDoc) => {
              await updateDoc(doc(db, "vendors", vendorDoc.id), {
                lastPurchasedDate: today
              });
            });
          } catch (e) {
            console.error("Failed to update vendor lastPurchasedDate:", e);
          }
        }
      }
      if (nextStatus === "Transport -> Client") {
        updates[`itemStatuses.${key}.shipDate`] = new Date().toISOString().split("T")[0];
        const currentBatches = shipment.shipmentsHistory || [];
        const isItemAlreadyInBatches = currentBatches.some(b => (b.items || []).some(bi => bi.id === item.id || bi.poItemId === item.id));
        if (!isItemAlreadyInBatches) {
          const newBatch = {
            id: `batch-${currentBatches.length + 1}-${Date.now()}`,
            batchNumber: currentBatches.length + 1,
            shippedAt: new Date().toISOString(),
            carrier: shipment.carrier || "N/A",
            trackingNumber: shipment.trackingNumber || "N/A",
            destination: shipment.destination,
            items: [{
              id: item.id,
              poItemId: item.id,
              lineNumber: item.lineNumber || "",
              name: item.name || item.description || "Item",
              description: item.description || "",
              quantity: Number(item.quantity || 0)
            }]
          };
          updates.shipmentsHistory = [...currentBatches, newBatch];
        }
      }
      await updateDoc(logRef, updates);

      // Trigger packing slip when status changes from Warehouse to Transport -> Client
      if (currentItemStatus === "Warehouse" && nextStatus === "Transport -> Client") {
        handleGeneratePackingSlip(shipment);
      }
    } catch (err) { console.error("Error advancing item status:", err); }
  }

  // Update a single item's status to a chosen state
  async function handleItemStatusChange(shipment, item, nextStatus, currentItemStatus) {
    if (nextStatus === currentItemStatus) return;

    // Intercept transition to Warehouse → open receiving modal for this item
    if (nextStatus === "Warehouse") {
      setReceiveContext({ shipment, item });
      setReceiveModalOpen(true);
      return;
    }

    // Intercept transition to Delivered → open delivery date confirmation modal
    if (nextStatus === "Delivered") {
      setDeliveryContext({ shipment, item, currentItemStatus });
      setDeliveryModalOpen(true);
      return;
    }

    // Intercept transition to Transport -> Client → trigger packing slip print preview
    if (nextStatus === "Transport -> Client") {
      handleGeneratePackingSlip(shipment);
    }

    try {
      const logRef = doc(db, "logistics", shipment.id);
      const key = item.vendorKey || item.id;
      const updates = { [`itemStatuses.${key}.status`]: nextStatus };
      
      // If changing to Purchased, update orderedDate
      if (nextStatus === "Purchased") {
        const today = new Date().toISOString().split("T")[0];
        if (!shipment.itemStatuses?.[key]?.orderedDate) {
          updates[`itemStatuses.${key}.orderedDate`] = today;
        }
        const vName = item.vendorName || item.vendor;
        if (vName) {
          try {
            const vendorsQuery = query(collection(db, "vendors"), where("name", "==", vName));
            const vendorsSnapshot = await getDocs(vendorsQuery);
            vendorsSnapshot.forEach(async (vendorDoc) => {
              await updateDoc(doc(db, "vendors", vendorDoc.id), {
                lastPurchasedDate: today
              });
            });
          } catch (e) {
            console.error("Failed to update vendor lastPurchasedDate:", e);
          }
        }
      }

      // If changing to Transport -> Client, auto-fill shipDate and add batch if not already present
      if (nextStatus === "Transport -> Client") {
        updates[`itemStatuses.${key}.shipDate`] = new Date().toISOString().split("T")[0];
        const currentBatches = shipment.shipmentsHistory || [];
        const isItemAlreadyInBatches = currentBatches.some(b => (b.items || []).some(bi => bi.id === item.id || bi.poItemId === item.id));
        if (!isItemAlreadyInBatches) {
          const newBatch = {
            id: `batch-${currentBatches.length + 1}-${Date.now()}`,
            batchNumber: currentBatches.length + 1,
            shippedAt: new Date().toISOString(),
            carrier: shipment.carrier || "N/A",
            trackingNumber: shipment.trackingNumber || "N/A",
            destination: shipment.destination,
            items: [{
              id: item.id,
              poItemId: item.id,
              lineNumber: item.lineNumber || "",
              name: item.name || item.description || "Item",
              description: item.description || "",
              quantity: Number(item.quantity || 0)
            }]
          };
          updates.shipmentsHistory = [...currentBatches, newBatch];
        }
      }
      
      await updateDoc(logRef, updates);
    } catch (err) {
      console.error("Error updating item status:", err);
    }
  }

  // Handle receiving a single item → advance to Warehouse + inventory sync
  async function handleReceiveItem(receivingData) {
    if (!receiveContext) return;
    const { shipment, item } = receiveContext;
    const { arrived, receivedQuantity, isFlagged, flagReason, zone, notes } = receivingData;
    const key = receivingData.itemKey || item.vendorKey || item.id;
    const poItemId = item.originalItemId || item.id;

    try {
      setSubmitting(true);
      const logRef = doc(db, "logistics", shipment.id);

      // Update item status + receiving data
      await updateDoc(logRef, {
        [`itemStatuses.${key}.status`]: "Warehouse",
        [`itemStatuses.${key}.receivingData`]: {
          arrived, receivedQuantity, isFlagged, flagReason, zone, notes,
          receivedDate: new Date().toISOString().split("T")[0]
        }
      });

      // Sync to Inventory
      if (arrived) {
        const invRef = collection(db, "inventory");
        const q = query(invRef, where("poId", "==", shipment.poId), where("poItemId", "==", key));
        const invSnap = await getDocs(q);

        const qtyTotal = item.vendorQty !== undefined ? item.vendorQty : item.quantity;
        const displayName = item.vendorName ? `${item.description || item.name || "Unknown Item"} (${item.vendorName})` : (item.description || item.name || "Unknown Item");

        const logParts = [`Received at Warehouse ${zone || "NC"}.`, `Qty: ${receivedQuantity}/${qtyTotal}`];
        if (isFlagged) logParts.push(`Flag: ${flagReason}`);
        if (notes) logParts.push(`Notes: ${notes}`);
        const logEntry = { text: logParts.join(" | "), type: "receiving", timestamp: new Date().toISOString() };

        if (!invSnap.empty) {
          const existingDoc = invSnap.docs[0];
          await updateDoc(doc(db, "inventory", existingDoc.id), {
            quantity: Number(receivedQuantity), zone: zone || "NC", logs: arrayUnion(logEntry)
          });
        } else {
          await addDoc(invRef, {
            sku: `INV-${Math.floor(10000 + Math.random() * 90000)}`, name: displayName,
            lineNumber: item.lineNumber || "",
            category: "Raw Materials", quantity: Number(receivedQuantity), warehouse: "NC",
            zone: zone || "NC", orderCount: Number(qtyTotal), poId: shipment.poId,
            poNumber: shipment.poNumber, clientName: shipment.clientName || "",
            poItemId: key, receivingSynced: true, logs: [logEntry]
          });
        }
      }

      setReceiveModalOpen(false);
      setReceiveContext(null);
    } catch (err) {
      console.error("Error receiving item:", err);
      alert("Failed to receive item.");
    } finally { setSubmitting(false); }
  }

  // Handle batch receiving submission for Progress Order Status
  async function handleReceiveBatchSubmit(receivingItems) {
    if (!receiveBatchContext) return;
    const { shipment, nonWarehouseUpdates } = receiveBatchContext;

    try {
      setSubmitting(true);
      const logRef = doc(db, "logistics", shipment.id);
      const updates = { ...(nonWarehouseUpdates || {}) };

      for (const rcv of receivingItems) {
        const key = rcv.vendorKey;
        const item = rcv.item;
        updates[`itemStatuses.${key}.status`] = "Warehouse";
        updates[`itemStatuses.${key}.receivingData`] = {
          arrived: rcv.arrived,
          receivedQuantity: rcv.receivedQuantity,
          isFlagged: rcv.isFlagged,
          flagReason: rcv.flagReason,
          zone: rcv.zone,
          notes: rcv.notes,
          receivedDate: new Date().toISOString().split("T")[0]
        };

        if (rcv.arrived) {
          const invRef = collection(db, "inventory");
          const q = query(invRef, where("poId", "==", shipment.poId), where("poItemId", "==", key));
          const invSnap = await getDocs(q);

          const qtyTotal = rcv.totalQuantity;
          const displayName = item.vendorName ? `${item.description || item.name || "Unknown Item"} (${item.vendorName})` : (item.description || item.name || "Unknown Item");

          const logParts = [`Received at Warehouse ${rcv.zone || "NC"}.`, `Qty: ${rcv.receivedQuantity}/${qtyTotal}`];
          if (rcv.isFlagged) logParts.push(`Flag: ${rcv.flagReason}`);
          if (rcv.notes) logParts.push(`Notes: ${rcv.notes}`);
          const logEntry = { text: logParts.join(" | "), type: "receiving", timestamp: new Date().toISOString() };

          if (!invSnap.empty) {
            const existingDoc = invSnap.docs[0];
            await updateDoc(doc(db, "inventory", existingDoc.id), {
              quantity: Number(rcv.receivedQuantity), zone: rcv.zone || "NC", logs: arrayUnion(logEntry)
            });
          } else {
            await addDoc(invRef, {
              sku: `INV-${Math.floor(10000 + Math.random() * 90000)}`, name: displayName,
              lineNumber: item.lineNumber || "",
              category: "Raw Materials", quantity: Number(rcv.receivedQuantity), warehouse: "NC",
              zone: rcv.zone || "NC", orderCount: Number(qtyTotal), poId: shipment.poId,
              poNumber: shipment.poNumber, clientName: shipment.clientName || "",
              poItemId: key, receivingSynced: true, logs: [logEntry]
            });
          }
        }
      }

      await updateDoc(logRef, updates);

      setReceiveBatchModalOpen(false);
      setReceiveBatchContext(null);
    } catch (err) {
      console.error("Error in batch receive submit:", err);
      alert("Failed to submit batch receiving.");
    } finally {
      setSubmitting(false);
    }
  }

  // Progress all items in a PO forward by one stage
  async function handleProgressOrderStatus(shipment) {
    const po = getPo(shipment.poId);
    const poItems = po?.items || [];
    if (!poItems.length) return;

    // Flatten all items and splits
    const shippingRows = [];
    poItems.forEach(item => {
      if (item.vendors && item.vendors.length > 0) {
        item.vendors.forEach(v => {
          shippingRows.push({
            ...item,
            vendorKey: `${item.id}_${v.id}`,
            vendorName: v.vendor,
            vendorQty: v.quantity,
            vendorCost: v.vendorPrice,
            vendorUrl: v.url,
            originalItemId: item.id,
            vendorId: v.id,
            isSplit: true
          });
        });
      } else {
        shippingRows.push({
          ...item,
          vendorKey: item.id,
          vendorName: item.vendor || "—",
          vendorQty: item.quantity || 0,
          vendorCost: item.vendorPrice || 0,
          vendorUrl: item.url || "",
          originalItemId: item.id,
          vendorId: "default",
          isSplit: false
        });
      }
    });

    const itemsToReceiveInWarehouse = [];
    const nonWarehouseUpdates = {};
    let shouldTriggerPackingSlip = false;
    const today = new Date().toISOString().split("T")[0];

    for (const row of shippingRows) {
      const currentStatus = getItemStatus(shipment, row.vendorKey);
      const currentIndex = statuses.indexOf(currentStatus);
      if (currentIndex === -1 || currentIndex >= statuses.length - 1) continue; // Already delivered or unknown

      const nextStatus = statuses[currentIndex + 1];

      if (nextStatus === "Warehouse") {
        itemsToReceiveInWarehouse.push(row);
      } else {
        nonWarehouseUpdates[`itemStatuses.${row.vendorKey}.status`] = nextStatus;

        if (nextStatus === "Purchased") {
          if (!shipment.itemStatuses?.[row.vendorKey]?.orderedDate) {
            nonWarehouseUpdates[`itemStatuses.${row.vendorKey}.orderedDate`] = today;
          }
          const vName = row.vendorName || row.vendor;
          if (vName) {
            try {
              const vendorsQuery = query(collection(db, "vendors"), where("name", "==", vName));
              const vendorsSnapshot = await getDocs(vendorsQuery);
              vendorsSnapshot.forEach(async (vendorDoc) => {
                await updateDoc(doc(db, "vendors", vendorDoc.id), { lastPurchasedDate: today });
              });
            } catch (e) {
              console.error("Failed to update vendor lastPurchasedDate:", e);
            }
          }
        }

        if (nextStatus === "Transport -> Client") {
          nonWarehouseUpdates[`itemStatuses.${row.vendorKey}.shipDate`] = today;
          shouldTriggerPackingSlip = true;
        }

        if (nextStatus === "Delivered") {
          nonWarehouseUpdates[`itemStatuses.${row.vendorKey}.deliveredDate`] = today;
        }
      }
    }

    if (itemsToReceiveInWarehouse.length > 0) {
      // Open multi-item receiving modal
      setReceiveBatchContext({
        shipment,
        itemsToReceive: itemsToReceiveInWarehouse,
        nonWarehouseUpdates
      });
      setReceiveBatchModalOpen(true);
      return;
    }

    // If no items going to Warehouse, commit updates immediately
    if (Object.keys(nonWarehouseUpdates).length > 0) {
      try {
        setSubmitting(true);
        const logRef = doc(db, "logistics", shipment.id);

        // Check if all items on the order are now delivered
        const updatedStatuses = { ...(shipment.itemStatuses || {}) };
        Object.entries(nonWarehouseUpdates).forEach(([path, val]) => {
          if (path.startsWith("itemStatuses.")) {
            const parts = path.split(".");
            if (parts.length === 3) {
              const itemKey = parts[1];
              const prop = parts[2];
              updatedStatuses[itemKey] = {
                ...(updatedStatuses[itemKey] || {}),
                [prop]: val
              };
            }
          }
        });

        const allDelivered = shippingRows.length > 0 && shippingRows.every(r => {
          const statVal = updatedStatuses[r.vendorKey] || updatedStatuses[r.id];
          const s = (typeof statVal === "object" ? statVal?.status : statVal) || shipment.status || "To Order";
          return s === "Delivered";
        });

        if (allDelivered) {
          nonWarehouseUpdates.status = "Delivered";
          nonWarehouseUpdates.deliveredDate = today;
          const targetPoId = shipment.poId || po?.id;
          if (targetPoId) {
            await updatePO(targetPoId, {
              deliveredDate: today
            });
          }
        }

        await updateDoc(logRef, nonWarehouseUpdates);

        if (shouldTriggerPackingSlip) {
          handleGeneratePackingSlip(shipment);
        }
      } catch (err) {
        console.error("Error progressing order statuses:", err);
        alert("Failed to progress order statuses.");
      } finally {
        setSubmitting(false);
      }
    }
  }

  async function handleDeliverySubmit(deliveryDate) {
    if (!deliveryContext) return;
    const { shipment, item } = deliveryContext;
    try {
      setSubmitting(true);
      const logRef = doc(db, "logistics", shipment.id);
      
      const key = item.vendorKey || item.id;
      const updates = { 
        [`itemStatuses.${key}.status`]: "Delivered",
        [`itemStatuses.${key}.deliveredDate`]: deliveryDate
      };
      
      // Compute if all other items are also delivered
      const po = pos.find(p => (shipment.poId && p.id === shipment.poId) || (shipment.poNumber && p.poNumber === shipment.poNumber));
      const poItems = po?.items || [];
      const updatedStatuses = { ...(shipment.itemStatuses || {}) };
      updatedStatuses[key] = { 
        ...(updatedStatuses[key] || {}), 
        status: "Delivered",
        deliveredDate: deliveryDate
      };
      
      const shippingRows = getPoShippingRows(poItems);
      const allDelivered = shippingRows.length > 0 && shippingRows.every(r => {
        const statVal = updatedStatuses[r.vendorKey] || updatedStatuses[r.id];
        const s = (typeof statVal === "object" ? statVal?.status : statVal) || shipment.status || "To Order";
        return s === "Delivered";
      });
      
      if (allDelivered) {
        updates.status = "Delivered";
        updates.deliveredDate = deliveryDate;
      }
      
      await updateDoc(logRef, updates);

      // Synchronize deliveredDate to PO
      const targetPoId = shipment.poId || po?.id;
      if (targetPoId && allDelivered) {
        await updatePO(targetPoId, {
          deliveredDate: deliveryDate
        });
      }

      setDeliveryModalOpen(false);
      setDeliveryContext(null);
    } catch (err) {
      console.error("Error setting delivery status:", err);
      alert("Failed to update delivery date.");
    } finally {
      setSubmitting(false);
    }
  }

  // Update carrier on a shipment
  async function handleCarrierUpdate(shipmentId, newCarrier) {
    try {
      await updateDoc(doc(db, "logistics", shipmentId), { carrier: newCarrier });
      setEditingCarrierId(null);
    } catch (err) { console.error("Error updating carrier:", err); }
  }

  const handleOpenCostModal = (shipment, batch, batchIndex, totalBatches) => {
    setEditCostContext({
      shipment,
      batch,
      batchIndex,
      totalBatches
    });
    setEditCostModalOpen(true);
  };

  const handleSaveShipmentCost = async (newCost) => {
    if (!editCostContext || !editCostContext.shipment) return;
    const { shipment, batch, batchIndex } = editCostContext;
    const numCost = parseFloat(newCost) || 0;

    let updatedHistory = shipment.shipmentsHistory ? [...shipment.shipmentsHistory] : [];
    let updatedShipmentCost = parseFloat(shipment.actualShippingCost) || 0;

    if (batch && batchIndex !== undefined && batchIndex !== null && updatedHistory[batchIndex]) {
      updatedHistory[batchIndex] = {
        ...updatedHistory[batchIndex],
        actualShippingCost: numCost
      };
      updatedShipmentCost = updatedHistory.reduce((sum, b) => sum + (parseFloat(b.actualShippingCost) || 0), 0);
    } else {
      updatedShipmentCost = numCost;
      if (updatedHistory.length === 1) {
        updatedHistory[0] = {
          ...updatedHistory[0],
          actualShippingCost: numCost
        };
      }
    }

    const logRef = doc(db, "logistics", shipment.id);
    await updateDoc(logRef, {
      actualShippingCost: updatedShipmentCost,
      shipmentsHistory: updatedHistory
    });

    try {
      await logSecurityEvent({
        action: "SHIPMENT_COST_UPDATED",
        category: "DATA_MODIFICATION",
        userId: currentUser?.uid || "anonymous",
        userEmail: currentUser?.email || "anonymous",
        resource: `logistics/${shipment.id}`,
        status: "SUCCESS",
        details: {
          poNumber: shipment.poNumber,
          batchIndex,
          newCost: numCost
        }
      });
    } catch (logErr) {
      console.error("Failed to log shipping cost update:", logErr);
    }
  };

  async function handleSubmit(e) {
    e.preventDefault();
    try {
      setSubmitting(true);
      const dataToSave = { ...formData };
      if (formData.status === "Purchased") { dataToSave.orderedDate = new Date().toISOString().split("T")[0]; }
      await addDoc(collection(db, "logistics"), dataToSave);
      setModalOpen(false);
    } catch (err) { console.error("Error creating shipment:", err); }
    finally { setSubmitting(false); }
  }

  const filteredShipments = shipments.filter(shipment => {
    const poObj = getPo(shipment.poId);
    
    // Filter by company assignment
    const matchesCompanyAssignment = (() => {
      if (!poObj) return false;
      const poLogoUrl = poObj.logoUrl || companyLogos.find(l => l.isActive)?.url || "";
      return companyLogos.some(logo => logo.url === poLogoUrl);
    })();
    if (!matchesCompanyAssignment) return false;

    if (companyFilter !== "All") {
      const companyName = getCompanyNameForPo(poObj);
      if (companyName !== companyFilter) return false;
    }

    const poItems = getPoItems(shipment.poId);
    const matchesSearch = shipment.trackingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          shipment.carrier?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          shipment.origin?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          shipment.destination?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          poItems.some(item => 
                            (item.description || item.name || "").toLowerCase().includes(searchTerm.toLowerCase())
                          );
    const matchesStatus = statusFilter === "All" || (() => {
      if (!poItems.length) return (shipment.status || "To Order") === statusFilter;
      return poItems.some(item => getItemStatus(shipment, item.id) === statusFilter);
    })();
    return matchesSearch && matchesStatus;
  });

  const activeShipments = filteredShipments.filter(s => !isShipmentDelivered(s, pos));

  const getStatusColor = (status) => {
    switch (status) {
      case "Delivered": return "var(--success)";
      case "Transport -> Client": return "var(--primary)";
      case "Warehouse": return "var(--accent)";
      case "Transport -> NC":
      case "Transport -> HQ": return "var(--primary)";
      case "Purchased": return "var(--text-secondary)";
      case "To Order": return "var(--text-muted)";
      default: return "var(--text-muted)";
    }
  };

  const getNextStatusLabel = (currentStatus) => {
    const normalized = currentStatus === "Transport -> HQ" ? "Transport -> NC" : currentStatus;
    const i = statuses.indexOf(normalized);
    return (i === -1 || i === statuses.length - 1) ? "" : statuses[i + 1];
  };

  return (
    <div className="animate-fade-in">
      <div className="page-header">
        <div className="page-header-info">
          <h1>Logistics & Shipments</h1>
          <p>Track cargo freight coordinates, dispatch carriers, and update transit statuses.</p>
        </div>
        <button className="btn btn-primary" onClick={handleOpenAddModal}>
          <Plus size={18} /> Dispatch Shipment
        </button>
      </div>

      {/* Filter and Search */}
      <div className="log-search-bar">
        <div className="log-search-box">
          <Search size={18} className="log-search-icon" />
          <input type="text" className="form-input log-search-input" placeholder="Search shipments..."
            value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
        </div>
        <div className="filter-select-group" style={{ display: "flex", gap: "1rem" }}>
          <select className="form-input filter-select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="All">All Statuses</option>
            {statuses.map(st => <option key={st} value={st}>{st}</option>)}
          </select>
          <select 
            className="form-input filter-select" 
            value={companyFilter} 
            onChange={(e) => setCompanyFilter(e.target.value)}
            style={{ backgroundColor: "var(--bg-card)" }}
          >
            <option value="All">All Companies</option>
            {companyLogos.map(logo => (
              <option key={logo.id} value={logo.name}>{logo.name}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Shipments Accordion */}
      <div className="log-accordion-list">
        {loading && shipments.length === 0 ? (
          <div className="log-loading">
            <Loader2 className="logistics-spinner" size={36} />
            <span>Locating telemetry feeds...</span>
          </div>
        ) : activeShipments.length === 0 ? (
          <div className="log-empty">
            <Truck size={48} className="log-empty-icon" />
            <h3>No Active Shipments</h3>
            <p>All shipments are delivered or no matching active shipments found.</p>
          </div>
        ) : (
          activeShipments.map(shipment => {
            const isExpanded = expandedShipments[shipment.id];
            const po = getPo(shipment.poId);
            const poItems = po?.items || [];
            const headerStatus = getHeaderStatus(shipment, poItems);
            const pureStatus = headerStatus.replace(" (Mixed)", "");

            return (
              <div key={shipment.id} className="log-card">
                {/* Header */}
                <div className={`log-card-header ${isExpanded ? "log-card-header-expanded" : ""}`} onClick={() => toggleAccordion(shipment.id)}>
                  <div className="log-card-header-left">
                    <div className="log-card-icon">
                      {getPoLogoUrl(po) ? (
                        <img 
                          src={getPoLogoUrl(po)} 
                          alt="Company logo" 
                          style={{ width: "22px", height: "22px", borderRadius: "4px", objectFit: "cover" }} 
                        />
                      ) : (
                        <Truck size={22} />
                      )}
                    </div>
                    <div className="log-card-info">
                      <div className="log-card-top-row">
                        {shipment.poNumber && <span className="sku-badge">{shipment.poNumber}</span>}
                        {(shipment.customerPoNumber || po?.customerPoNumber) && (
                          <span style={{ fontSize: "0.85rem", color: "var(--text-secondary)", display: "inline-flex", alignItems: "center", gap: "0.25rem" }}>
                            Cust PO: <strong style={{ color: "var(--text-primary)" }}>{shipment.customerPoNumber || po?.customerPoNumber}</strong>
                          </span>
                        )}
                        {po?.isPriority && <span className="priority-pill">PRIORITY</span>}
                        {po && (
                          <span 
                            className="sku-badge log-proj-sh-badge" 
                            title={`Projected Shipping & Handling from Estimate: $${getPoShippingCostOut(po).toFixed(2)}`}
                            style={{
                              background: "rgba(147, 51, 234, 0.12)",
                              color: "var(--primary)",
                              border: "1px solid rgba(147, 51, 234, 0.25)",
                              fontWeight: 600,
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "0.25rem",
                              fontSize: "0.78rem"
                            }}
                          >
                            Proj S/H: ${getPoShippingCostOut(po).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </span>
                        )}
                        {po?.title && <span className="log-card-project">{po.title}</span>}
                        {/* Editable carrier */}
                        {editingCarrierId === shipment.id ? (
                          <select className="form-input log-carrier-select" value={shipment.carrier}
                            onClick={(e) => e.stopPropagation()}
                            onChange={(e) => handleCarrierUpdate(shipment.id, e.target.value)}
                            onBlur={() => setEditingCarrierId(null)} autoFocus>
                            {carriers.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        ) : (
                          <span className="log-card-carrier log-card-carrier-editable"
                            onClick={(e) => { e.stopPropagation(); setEditingCarrierId(shipment.id); }}>
                            {shipment.carrier} <Edit2 size={11} />
                          </span>
                        )}
                        {shipment.orderedDate && <span className="log-card-ordered">Ordered: {shipment.orderedDate}</span>}
                        {(() => {
                          const { primaryShipment, batches, totalBatches } = getAllPackingSlipsForPO({ po, shipment, shipments });
                          const resolvedShipment = primaryShipment || shipment;

                          if (batches && batches.length > 0) {
                            return (
                              <div style={{ display: "inline-flex", gap: "0.5rem", marginLeft: "0.75rem", verticalAlign: "middle", flexWrap: "wrap", alignItems: "center" }}>
                                <div style={{ display: "inline-flex", gap: "0.35rem", alignItems: "center" }}>
                                  {batches.map((batch, idx) => (
                                    <button
                                      key={batch.id || idx}
                                      type="button"
                                      className="packing-slip-print-btn"
                                      title={`Print Packing Slip for Shipment #${idx + 1}\nCarrier: ${batch.carrier || "N/A"}\nTracking: ${batch.trackingNumber || "N/A"}\nDate: ${batch.shippedAt ? new Date(batch.shippedAt).toLocaleDateString() : "N/A"}\nItems: ${batch.items?.length || 0}`}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        handleGeneratePackingSlip(resolvedShipment, batch, idx, totalBatches);
                                      }}
                                      style={{
                                        background: "rgba(0, 224, 150, 0.12)",
                                        border: "1px solid rgba(0, 224, 150, 0.25)",
                                        borderRadius: "4px",
                                        padding: "0.15rem 0.35rem",
                                        cursor: "pointer",
                                        color: "#00e096",
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "0.25rem",
                                        fontSize: "0.72rem",
                                        fontWeight: "600"
                                      }}
                                    >
                                      <FileText size={11} />
                                      Slip #{idx + 1}
                                    </button>
                                  ))}
                                </div>

                                <div style={{ display: "inline-flex", gap: "0.35rem", alignItems: "center" }}>
                                  {totalBatches === 1 ? (() => {
                                    const cost = batches[0]?.actualShippingCost !== undefined && batches[0]?.actualShippingCost !== null
                                      ? Number(batches[0].actualShippingCost)
                                      : (Number(resolvedShipment?.actualShippingCost) || 0);
                                    const costText = cost > 0 ? `$${cost.toFixed(2)}` : "Set Cost";
                                    return (
                                      <button
                                        type="button"
                                        className="ship-cost-tag-btn"
                                        title={`Click to view/edit shipping cost ($${cost.toFixed(2)}) for full shipment`}
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          handleOpenCostModal(resolvedShipment, batches[0], 0, 1);
                                        }}
                                        style={{
                                          background: "rgba(59, 130, 246, 0.12)",
                                          border: "1px solid rgba(59, 130, 246, 0.25)",
                                          borderRadius: "4px",
                                          padding: "0.15rem 0.35rem",
                                          cursor: "pointer",
                                          color: "#3b82f6",
                                          display: "inline-flex",
                                          alignItems: "center",
                                          gap: "0.25rem",
                                          fontSize: "0.72rem",
                                          fontWeight: "600"
                                        }}
                                      >
                                        <DollarSign size={11} />
                                        Ship Cost: {costText}
                                      </button>
                                    );
                                  })() : (
                                    batches.map((batch, idx) => {
                                      const cost = batch.actualShippingCost !== undefined && batch.actualShippingCost !== null
                                        ? Number(batch.actualShippingCost)
                                        : 0;
                                      const costText = cost > 0 ? `$${cost.toFixed(2)}` : "Set Cost";
                                      return (
                                        <button
                                          key={batch.id ? `cost-${batch.id}` : `cost-${idx}`}
                                          type="button"
                                          className="ship-cost-tag-btn"
                                          title={`Click to view/edit shipping cost ($${cost.toFixed(2)}) for Shipment #${idx + 1}`}
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            handleOpenCostModal(resolvedShipment, batch, idx, totalBatches);
                                          }}
                                          style={{
                                            background: "rgba(59, 130, 246, 0.12)",
                                            border: "1px solid rgba(59, 130, 246, 0.25)",
                                            borderRadius: "4px",
                                            padding: "0.15rem 0.35rem",
                                            cursor: "pointer",
                                            color: "#3b82f6",
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: "0.25rem",
                                            fontSize: "0.72rem",
                                            fontWeight: "600"
                                          }}
                                        >
                                          <DollarSign size={11} />
                                          Ship Cost #{idx + 1}: {costText}
                                        </button>
                                      );
                                    })
                                  )}
                                </div>
                              </div>
                            );
                          }

                          const hasShippedItems = poItems.some(item => {
                            const itemStatus = shipment.itemStatuses?.[item.id]?.status || shipment.status || "To Order";
                            return itemStatus === "Transport -> Client" || itemStatus === "Delivered";
                          });
                          if (!hasShippedItems) return null;

                          const singleCost = Number(shipment.actualShippingCost) || 0;
                          const singleCostText = singleCost > 0 ? `$${singleCost.toFixed(2)}` : "Set Cost";

                          return (
                            <div style={{ display: "inline-flex", gap: "0.35rem", marginLeft: "0.75rem", verticalAlign: "middle", alignItems: "center" }}>
                              <button
                                type="button"
                                className="packing-slip-print-btn"
                                title="Print Packing Slip"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleGeneratePackingSlip(shipment);
                                }}
                                style={{
                                  background: "rgba(0, 224, 150, 0.12)",
                                  border: "1px solid rgba(0, 224, 150, 0.25)",
                                  borderRadius: "4px",
                                  padding: "0.15rem 0.35rem",
                                  cursor: "pointer",
                                  color: "#00e096",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                  fontSize: "0.72rem",
                                  fontWeight: "600"
                                }}
                              >
                                <FileText size={11} />
                                Slip
                              </button>
                              <button
                                type="button"
                                className="ship-cost-tag-btn"
                                title={`Click to view/edit shipping cost ($${singleCost.toFixed(2)}) for shipment`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleOpenCostModal(shipment, null, null, 1);
                                }}
                                style={{
                                  background: "rgba(59, 130, 246, 0.12)",
                                  border: "1px solid rgba(59, 130, 246, 0.25)",
                                  borderRadius: "4px",
                                  padding: "0.15rem 0.35rem",
                                  cursor: "pointer",
                                  color: "#3b82f6",
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "0.25rem",
                                  fontSize: "0.72rem",
                                  fontWeight: "600"
                                }}
                              >
                                <DollarSign size={11} />
                                Ship Cost: {singleCostText}
                              </button>
                            </div>
                          );
                        })()}
                      </div>
                      <div className="log-card-route">
                        <span className="log-route-origin">{shipment.origin}</span>
                        <span className="log-route-arrow">→</span>
                        <span className="log-route-dest">{shipment.destination}</span>
                      </div>
                    </div>
                  </div>
                  <div className="log-card-header-right">
                    <span className="log-card-status" style={{ color: getStatusColor(pureStatus) }}>{headerStatus}</span>
                    <span className="log-card-eta">{shipment.estimatedDelivery}</span>
                    {isExpanded ? <ChevronUp size={18} color="var(--text-secondary)" /> : <ChevronDown size={18} color="var(--text-secondary)" />}
                  </div>
                </div>

                {/* Body — Per-Item Status Table */}
                {isExpanded && (
                  <div className="log-card-body">
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem", flexWrap: "wrap", gap: "0.5rem" }}>
                      <h4 className="log-body-title" style={{ margin: 0 }}>Line Items</h4>
                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                        {po && (
                          <button
                            type="button"
                            className="btn btn-warning"
                            disabled={isPoFullyDelivered(po)}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedChangeRequestPo(po);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.4rem",
                              padding: "0.3rem 0.6rem",
                              fontSize: "0.8rem",
                              minHeight: "auto",
                              color: "#000000",
                              opacity: isPoFullyDelivered(po) ? 0.4 : 1,
                              cursor: isPoFullyDelivered(po) ? "not-allowed" : "pointer"
                            }}
                            title={isPoFullyDelivered(po) ? "All line items have been delivered. No changes can be made." : "Open Change Request modal"}
                          >
                            <Sliders size={12} color="#000000" />
                            Change Request
                          </button>
                        )}
                        {(po || shipment.poNumber) && (
                          <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedTasksPo(po || { id: shipment.poId, poNumber: shipment.poNumber, clientName: shipment.customerName || shipment.clientName });
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "0.4rem",
                              padding: "0.3rem 0.6rem",
                              fontSize: "0.8rem",
                              minHeight: "auto"
                            }}
                            title="View & manage tasks for this PO"
                          >
                            <CheckSquare size={12} />
                            Tasks
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-primary"
                          disabled={isShipmentDelivered(shipment, pos) || submitting}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleProgressOrderStatus(shipment);
                          }}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.4rem",
                            padding: "0.3rem 0.75rem",
                            fontSize: "0.8rem",
                            minHeight: "auto",
                            backgroundColor: "var(--primary)",
                            borderColor: "var(--primary)",
                            color: "#ffffff",
                            opacity: isShipmentDelivered(shipment, pos) ? 0.4 : 1,
                            cursor: isShipmentDelivered(shipment, pos) ? "not-allowed" : "pointer"
                          }}
                          title={isShipmentDelivered(shipment, pos) ? "All items on this order have been delivered." : "Advance all line items forward by one stage"}
                        >
                          <Truck size={13} />
                          Progress Order Status
                        </button>
                      </div>
                    </div>
                    {poItems.length === 0 ? (
                      <p className="log-no-items">{shipment.poId ? "No line items found for this PO." : "This shipment is not linked to a PO."}</p>
                    ) : (
                      (() => {
                        const shippingRows = [];
                        poItems.forEach(item => {
                          if (item.vendors && item.vendors.length > 0) {
                            item.vendors.forEach(v => {
                              shippingRows.push({
                                ...item,
                                vendorKey: `${item.id}_${v.id}`,
                                vendorName: v.vendor,
                                vendorQty: v.quantity,
                                vendorCost: v.vendorPrice,
                                vendorUrl: v.url,
                                originalItemId: item.id,
                                vendorId: v.id,
                                isSplit: true
                              });
                            });
                          } else {
                            shippingRows.push({
                              ...item,
                              vendorKey: item.id,
                              vendorName: item.vendor || "—",
                              vendorQty: item.quantity || 0,
                              vendorCost: item.vendorPrice || 0,
                              vendorUrl: item.url || "",
                              originalItemId: item.id,
                              vendorId: "default",
                              isSplit: false
                            });
                          }
                        });

                        return (
                          <>
                            {/* Desktop Table View (≥ 769px) */}
                            <div className="log-items-table-wrap">
                              <table className="log-items-table">
                                <thead>
                                  <tr>
                                    <th>Description</th>
                                    <th>Vendor</th>
                                    <th style={{ textAlign: "center" }}>Qty</th>
                                    <th>Tracking #</th>
                                    <th>Status</th>
                                    <th>Ship Date</th>
                                    <th>Delivered Date</th>
                                    <th>Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {shippingRows.map((row, idx) => {
                                    const itemStatus = getItemStatus(shipment, row.vendorKey);
                                    return (
                                      <tr key={idx} className={itemStatus === "Delivered" ? "log-row-delivered" : ""}>
                                        <td>{row.lineNumber ? `${row.lineNumber} - ` : ""}{row.description || row.name || "Unnamed Item"}</td>
                                        <td className="log-td-vendor">{row.vendorName || "—"}</td>
                                        <td style={{ textAlign: "center" }}>{parseFloat(row.vendorQty) || 0}</td>
                                        <td>
                                          <TrackingNumberCell
                                            shipmentId={shipment.id}
                                            itemId={row.vendorKey}
                                            initialValue={shipment.itemStatuses?.[row.vendorKey]?.trackingNumber}
                                            defaultPlaceholder={shipment.trackingNumber}
                                          />
                                        </td>
                                        <td>
                                          <StatusCell
                                            shipment={shipment}
                                            item={row}
                                            initialValue={itemStatus}
                                            handleItemStatusChange={handleItemStatusChange}
                                            statuses={statuses}
                                            getStatusColor={getStatusColor}
                                          />
                                        </td>
                                        <td>
                                          <ShipDateCell
                                            shipmentId={shipment.id}
                                            itemId={row.vendorKey}
                                            initialValue={shipment.itemStatuses?.[row.vendorKey]?.shipDate}
                                            editable={itemStatus === "Transport -> Client" || itemStatus === "Delivered"}
                                          />
                                        </td>
                                        <td>
                                          <DeliveredDateCell
                                            shipmentId={shipment.id}
                                            itemId={row.vendorKey}
                                            initialValue={shipment.itemStatuses?.[row.vendorKey]?.deliveredDate}
                                            editable={itemStatus === "Delivered"}
                                          />
                                        </td>
                                        <td>
                                          {itemStatus !== "Delivered" ? (
                                            <button className="btn btn-secondary log-advance-btn"
                                              onClick={() => handleItemAdvance(shipment, row, itemStatus)}>
                                              → {getNextStatusLabel(itemStatus)}
                                            </button>
                                          ) : (
                                            <span className="log-delivered-badge">✓</span>
                                          )}
                                        </td>
                                      </tr>
                                    );
                                  })}
                                </tbody>
                              </table>
                            </div>

                            {/* Mobile Card List View (< 769px) */}
                            <div className="log-items-mobile-list">
                              {shippingRows.map((row, idx) => {
                                const itemStatus = getItemStatus(shipment, row.vendorKey);
                                return (
                                  <div key={`mob-${idx}`} className={`log-item-mobile-card ${itemStatus === "Delivered" ? "log-row-delivered" : ""}`}>
                                    <div className="log-item-mobile-header">
                                      <div>
                                        <div className="log-item-mobile-title">
                                          {row.lineNumber ? `${row.lineNumber} - ` : ""}{row.description || row.name || "Unnamed Item"}
                                        </div>
                                        <div className="log-item-mobile-sub">
                                          Vendor: <strong>{row.vendorName || "—"}</strong>
                                        </div>
                                      </div>
                                      <span className="sku-badge" style={{ fontSize: "0.78rem" }}>
                                        Qty: {parseFloat(row.vendorQty) || 0}
                                      </span>
                                    </div>

                                    <div className="log-item-mobile-grid">
                                      <div className="log-item-mobile-field">
                                        <label>Tracking #</label>
                                        <TrackingNumberCell
                                          shipmentId={shipment.id}
                                          itemId={row.vendorKey}
                                          initialValue={shipment.itemStatuses?.[row.vendorKey]?.trackingNumber}
                                          defaultPlaceholder={shipment.trackingNumber}
                                        />
                                      </div>
                                      <div className="log-item-mobile-field">
                                        <label>Ship Date</label>
                                        <ShipDateCell
                                          shipmentId={shipment.id}
                                          itemId={row.vendorKey}
                                          initialValue={shipment.itemStatuses?.[row.vendorKey]?.shipDate}
                                          editable={itemStatus === "Transport -> Client" || itemStatus === "Delivered"}
                                        />
                                      </div>
                                      <div className="log-item-mobile-field">
                                        <label>Delivered Date</label>
                                        <DeliveredDateCell
                                          shipmentId={shipment.id}
                                          itemId={row.vendorKey}
                                          initialValue={shipment.itemStatuses?.[row.vendorKey]?.deliveredDate}
                                          editable={itemStatus === "Delivered"}
                                        />
                                      </div>
                                    </div>

                                    <div className="log-item-mobile-actions">
                                      <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                                        <span style={{ fontSize: "0.72rem", color: "var(--text-muted)", textTransform: "uppercase", fontWeight: 600 }}>Status:</span>
                                        <StatusCell
                                          shipment={shipment}
                                          item={row}
                                          initialValue={itemStatus}
                                          handleItemStatusChange={handleItemStatusChange}
                                          statuses={statuses}
                                          getStatusColor={getStatusColor}
                                        />
                                      </div>
                                      {itemStatus !== "Delivered" ? (
                                        <button 
                                          type="button"
                                          className="btn btn-secondary log-advance-btn"
                                          onClick={() => handleItemAdvance(shipment, row, itemStatus)}
                                          style={{ padding: "0.35rem 0.65rem", fontSize: "0.8rem" }}
                                        >
                                          → {getNextStatusLabel(itemStatus)}
                                        </button>
                                      ) : (
                                        <span className="log-delivered-badge" style={{ fontSize: "0.9rem" }}>✓ Delivered</span>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </>
                        );
                      })()
                    )}
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>

      {/* Modals */}
      <DispatchShipmentModal isOpen={modalOpen} onClose={() => setModalOpen(false)} formData={formData}
        setFormData={setFormData} carriers={carriers} statuses={statuses} loading={submitting} onSubmit={handleSubmit} />

      <ReceiveItemModal isOpen={receiveModalOpen} onClose={() => { setReceiveModalOpen(false); setReceiveContext(null); }}
        item={receiveContext?.item} zones={zones} loading={submitting} onSubmit={handleReceiveItem} />

      <ReceiveBatchModal
        isOpen={receiveBatchModalOpen}
        onClose={() => { setReceiveBatchModalOpen(false); setReceiveBatchContext(null); }}
        shipment={receiveBatchContext?.shipment}
        itemsToReceive={receiveBatchContext?.itemsToReceive}
        zones={zones}
        loading={submitting}
        onSubmit={handleReceiveBatchSubmit}
      />

      <DeliveryDateModal isOpen={deliveryModalOpen} onClose={() => { setDeliveryModalOpen(false); setDeliveryContext(null); }}
        itemName={deliveryContext?.item?.description || deliveryContext?.item?.name} onSubmit={handleDeliverySubmit} />

      {selectedChangeRequestPo && (
        <ChangeRequestModal
          po={selectedChangeRequestPo}
          onClose={() => setSelectedChangeRequestPo(null)}
        />
      )}

      {selectedTasksPo && (
        <POTasksModal
          isOpen={!!selectedTasksPo}
          po={selectedTasksPo}
          onClose={() => setSelectedTasksPo(null)}
        />
      )}

      <EditShippingCostModal
        isOpen={editCostModalOpen}
        onClose={() => {
          setEditCostModalOpen(false);
          setEditCostContext(null);
        }}
        shipment={editCostContext?.shipment}
        batch={editCostContext?.batch}
        batchIndex={editCostContext?.batchIndex}
        totalBatches={editCostContext?.totalBatches || 1}
        po={pos.find(p => (editCostContext?.shipment?.poId && p.id === editCostContext.shipment.poId) || (editCostContext?.shipment?.poNumber && p.poNumber === editCostContext.shipment.poNumber))}
        currentCost={editCostContext?.batch?.actualShippingCost ?? editCostContext?.shipment?.actualShippingCost ?? 0}
        onSave={handleSaveShipmentCost}
      />
    </div>
  );
}

function TrackingNumberCell({ shipmentId, itemId, initialValue, defaultPlaceholder }) {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  const handleBlur = async () => {
    if (value === initialValue) return;
    try {
      const logRef = doc(db, "logistics", shipmentId);
      await updateDoc(logRef, {
        [`itemStatuses.${itemId}.trackingNumber`]: value
      });
    } catch (err) {
      console.error("Error saving tracking number:", err);
    }
  };

  return (
    <input
      type="text"
      className="form-input log-cell-input"
      value={value}
      placeholder={defaultPlaceholder || "No Tracking #"}
      onChange={(e) => setValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={(e) => {
        if (e.key === "Enter") {
          e.target.blur();
        }
      }}
    />
  );
}

function ShipDateCell({ shipmentId, itemId, initialValue, editable }) {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  const handleUpdate = async (newValue) => {
    setValue(newValue);
    try {
      const logRef = doc(db, "logistics", shipmentId);
      await updateDoc(logRef, {
        [`itemStatuses.${itemId}.shipDate`]: newValue
      });
    } catch (err) {
      console.error("Error saving ship date:", err);
    }
  };

  if (!editable) {
    return <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{value || "—"}</span>;
  }

  return (
    <input
      type="date"
      className="form-input log-cell-input"
      value={value}
      onChange={(e) => handleUpdate(e.target.value)}
    />
  );
}

function DeliveredDateCell({ shipmentId, itemId, initialValue, editable }) {
  const [value, setValue] = useState(initialValue || "");

  useEffect(() => {
    setValue(initialValue || "");
  }, [initialValue]);

  const handleUpdate = async (newValue) => {
    setValue(newValue);
    try {
      const logRef = doc(db, "logistics", shipmentId);
      await updateDoc(logRef, {
        [`itemStatuses.${itemId}.deliveredDate`]: newValue
      });
    } catch (err) {
      console.error("Error saving delivered date:", err);
    }
  };

  if (!editable) {
    return <span style={{ color: "var(--text-muted)", fontSize: "0.85rem" }}>{value || "—"}</span>;
  }

  return (
    <input
      type="date"
      className="form-input log-cell-input"
      value={value}
      onChange={(e) => handleUpdate(e.target.value)}
    />
  );
}

function StatusCell({ shipment, item, initialValue, handleItemStatusChange, statuses, getStatusColor }) {
  const [isEditing, setIsEditing] = useState(false);

  if (isEditing) {
    return (
      <select
        value={initialValue}
        autoFocus
        onBlur={() => setIsEditing(false)}
        onChange={async (e) => {
          setIsEditing(false);
          await handleItemStatusChange(shipment, item, e.target.value, initialValue);
        }}
        className="form-input log-item-status-select log-cell-input"
        style={{
          color: getStatusColor(initialValue),
          fontWeight: 600
        }}
      >
        {statuses.map(st => (
          <option key={st} value={st} style={{ color: "var(--text-primary)", backgroundColor: "var(--bg-card)" }}>
            {st}
          </option>
        ))}
      </select>
    );
  }

  return (
    <span 
      className="log-item-status-badge" 
      style={{ color: getStatusColor(initialValue), cursor: "pointer", display: "inline-block", padding: "0.2rem 0.4rem" }}
      onClick={() => setIsEditing(true)}
      title="Click to change status"
    >
      {initialValue}
    </span>
  );
}


